import { useEffect, useRef, type CSSProperties } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, BadgeCheck, ClipboardList, CreditCard,
  Headset, Languages, Lock, Mail, MessageCircle,
  ShieldCheck, SlidersHorizontal, Sparkles, Workflow, Zap, type LucideIcon,
} from "lucide-react";
import { hongKongContactPath, hongKongDownloadPath } from "../../routes";
import { reportLeadEntryConversion } from "../../tracking";
import { DownloadLink } from "../DownloadLink";
import { HomeProductPreview, type ProductPreviewKind } from "./HomeProductPreview";
import freePlanArtwork from "../../assets/home/free-plan-vintage-botanical-v11.png";

/* 「為什麼選擇 ClawEase」— 參考 twt.com/chat 的 why 板塊：五張全寬卡片隨捲動
   逐張 sticky 疊起，被蓋住的卡片以底部為圓心微微縮小，形成一疊牌的層次。
   左側文案與要點，右側依主題呈現方案價格或匿名接待示例。 */

type WhyCard = {
  title: string;
  desc: string;
  preview: "pricing" | ProductPreviewKind;
  chips: { icon: LucideIcon; color: string; text: string }[];
};

const CARDS: WhyCard[] = [
  {
    title: "永久免費開始，專業團隊護航",
    desc: "Free 方案無需訂閱費、無需信用卡，由基本查詢與預約開始。需要度身定制？專業交付團隊隨時候命，按你的業務需要調整每個細節。",
    preview: "pricing",
    chips: [
      { icon: BadgeCheck, color: "#059669", text: "US$0 月費" },
      { icon: CreditCard, color: "#4f46e5", text: "無需信用卡" },
      { icon: Headset, color: "#7c3aed", text: "專業交付團隊" },
    ],
  },
  {
    title: "廣東話優先，地道香港體驗",
    desc: "由廣東話出發，支援普通話、英語等多種語言，隨客人的語言自然切換。聽得明香港客人的日常講法，回覆自然流利。",
    preview: "chat",
    chips: [
      { icon: Languages, color: "#7c3aed", text: "廣東話優先" },
      { icon: MessageCircle, color: "#4f46e5", text: "多語自然切換" },
      { icon: Sparkles, color: "#db2777", text: "地道表達" },
    ],
  },
  {
    title: "AI 編排工作流，小白也能上手",
    desc: "由接聽、理解需求、查閱日曆到發出確認，每個接待步驟都可按你的業務流程編排。無需寫代碼，簡單設定即可上線。",
    preview: "workflow",
    chips: [
      { icon: Workflow, color: "#7c3aed", text: "無需寫代碼" },
      { icon: SlidersHorizontal, color: "#d97706", text: "簡單設定" },
      { icon: Zap, color: "#059669", text: "快速上手" },
    ],
  },
  {
    title: "多重安全防護，資料歸你掌控",
    desc: "以 HTTPS 保護資料傳輸，電話、電郵及日曆的連接憑證採用 AES-256-GCM 加密儲存。工作執行保留結果與明細，方便查閱及追溯。",
    preview: "security",
    chips: [
      { icon: ShieldCheck, color: "#4f46e5", text: "HTTPS 傳輸" },
      { icon: Lock, color: "#059669", text: "憑證加密儲存" },
      { icon: ClipboardList, color: "#7c3aed", text: "執行可追蹤" },
    ],
  },
  {
    title: "執行有界線，難題可轉人工",
    desc: "按照設定的規則與授權範圍執行，需確認的步驟先取得確認再繼續。遇到超出能力範圍或客人要求真人時，可將對話轉交人工接手。",
    preview: "control",
    chips: [
      { icon: ShieldCheck, color: "#7c3aed", text: "規則約束" },
      { icon: BadgeCheck, color: "#059669", text: "關鍵步驟確認" },
      { icon: Headset, color: "#d97706", text: "支援轉人工" },
    ],
  },
];

function FreePlanPreview() {
  return (
    <div className="ce-product-stage">
      <div className="ce-free-artwork" role="group" aria-label="Free 方案，月費 US$0">
        <div className="ce-free-window-bar" aria-hidden="true">
          <i /><i /><i /><span />
        </div>
        <div className="ce-free-canvas">
          <img
            src={freePlanArtwork}
            width={1448}
            height={1086}
            loading="lazy"
            decoding="async"
            alt=""
          />
        </div>
      </div>
    </div>
  );
}

export function HomeWhyClawEase() {
  const { search } = useLocation();
  const stackRef = useRef<HTMLDivElement>(null);

  /* 捲動時為被覆蓋的卡片加上 scale 深度提示：每多一張卡蓋上來就按進度縮小
     0.006，transform-origin 在底部，與 sticky 疊放配合出「一疊牌」效果。
     最後一張卡到達槽位後，整疊切換為 relative + 固定 translateY：五張卡保持
     各自的層疊偏移、像普通內容一樣整體向上離場，而不是交給 sticky 底部夾持
     （那會按槽位先後逐張脫隊，看起來只有最後一張卡單獨往上滑）。
     rAF 節流；prefers-reduced-motion 時停用 scale，但整體離場仍適用。 */
  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const cards = Array.from(stack.querySelectorAll(".ce-why-card")) as HTMLElement[];
    const last = cards.length - 1;
    const resetInlineStyles = () => {
      /* 先還原卡片再量測——熱更新/尺寸變化後可能殘留 relative + top:0，
         直接量 getComputedStyle().top 會拿到 0 而不是槽位，導致切換瞬移。 */
      cards.forEach((card) => { card.style.position = ""; card.style.top = ""; card.style.transform = ""; });
    };
    resetInlineStyles();
    let stickyTops = cards.map((card) => parseFloat(getComputedStyle(card).top) || 0);
    let flowTops = new Array<number>(cards.length).fill(NaN);
    let released = false;
    let raf = 0;
    const update = () => {
      raf = 0;
      const scrollY = window.scrollY;
      let rects = cards.map((card) => card.getBoundingClientRect());
      /* 只在卡片尚未 pin 住、也未帶 transform 時刷新它的 flow 位置——pinned
         或已被夾持的 getBoundingClientRect 不是 flow 值；而 pinned 卡帶著
         scale 變換（圓心在底部會把視覺頂邊往下推幾 px），會騙過 top 判斷
         把 pin 住的當成 flow 量進去，切換瞬間 translateY 歸零 → 卡片瞬移
         閃跳。transform 為空串才是真正的純 flow 狀態。 */
      if (!released) {
        cards.forEach((card, i) => {
          if (card.style.transform === "" && rects[i].top > stickyTops[i] + 1) flowTops[i] = rects[i].top + scrollY;
        });
      }
      const releaseAt = flowTops[last] - stickyTops[last];
      /* 五張卡的 flow 位置全部量齊才允許切換；任何一張是 NaN 時 translateY
         會算出 NaN 被瀏覽器丟棄，卡片瞬移回流位——寧可保持 sticky 自然行為。 */
      const shouldRelease = Number.isFinite(releaseAt) && flowTops.every(Number.isFinite) && scrollY >= releaseAt;
      if (shouldRelease !== released) {
        released = shouldRelease;
        cards.forEach((card, i) => {
          if (released) {
            card.style.position = "relative";
            card.style.top = "0px";
            const settledScale = 1 - 0.006 * (last - i);
            card.style.transform = `translateY(${(releaseAt + stickyTops[i] - flowTops[i]).toFixed(1)}px) scale(${settledScale.toFixed(4)})`;
          } else {
            card.style.position = "";
            card.style.top = "";
            card.style.transform = "";
          }
        });
        if (!released) rects = cards.map((card) => card.getBoundingClientRect());
      }
      if (released || reduce.matches) return;
      const vh = window.innerHeight;
      cards.forEach((card, i) => {
        let scale = 1;
        for (let j = i + 1; j < cards.length; j += 1) {
          const progress = Math.min(1, Math.max(0, 1 - (rects[j].top - stickyTops[j]) / Math.max(1, vh - stickyTops[j])));
          scale -= 0.006 * progress;
        }
        card.style.transform = scale >= 0.999 ? "" : `scale(${scale.toFixed(4)})`;
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    const onResize = () => {
      /* 斷點切換會改變槽位間距與卡高：先還原 sticky 再重新量測。 */
      released = false;
      cards.forEach((card) => { card.style.position = ""; card.style.top = ""; card.style.transform = ""; });
      stickyTops = cards.map((card) => parseFloat(getComputedStyle(card).top) || 0);
      flowTops = new Array<number>(cards.length).fill(NaN);
      onScroll();
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* sticky 卡片會彼此重疊，單用 IntersectionObserver 會把被蓋住的卡片也
     判定為可見。改以卡片越過視口下方觸發線的先後找出當前卡片，讓只有
     當前卡片的三個賣點依次淡入；離開後重置，捲回時可再次播放。 */
  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    const badgeLists = Array.from(stack.querySelectorAll(".ce-why-badges")) as HTMLElement[];
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (reduce.matches) {
      badgeLists.forEach((list) => list.classList.add("is-visible"));
      return;
    }

    badgeLists.forEach((list) => list.classList.add("has-reveal"));
    let raf = 0;
    const update = () => {
      raf = 0;
      const triggerY = window.innerHeight * .88;
      let active = -1;
      badgeLists.forEach((list, index) => {
        const rect = list.getBoundingClientRect();
        if (rect.top <= triggerY && rect.bottom > 0) active = index;
      });
      badgeLists.forEach((list, index) => list.classList.toggle("is-visible", index === active));
    };
    const onViewportChange = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onViewportChange, { passive: true });
    window.addEventListener("resize", onViewportChange);
    return () => {
      window.removeEventListener("scroll", onViewportChange);
      window.removeEventListener("resize", onViewportChange);
      if (raf) cancelAnimationFrame(raf);
      badgeLists.forEach((list) => list.classList.remove("has-reveal", "is-visible"));
    };
  }, []);

  return (
    <>
      <div className="ce-hk-section-inner ce-hk-how-head">
        <h2>為什麼選擇 ClawEase？</h2>
        <p className="ce-hk-section-lede">由接待來電、安排預約到更新記錄，照顧香港中小企的日常需要。</p>
      </div>
      <div className="ce-hk-section-inner">
        <div className="ce-why-stack" ref={stackRef}>
          {CARDS.map((card, index) => (
            <article key={card.preview} className={`ce-why-card ce-why-card--${card.preview}`} style={{ "--d": index } as CSSProperties}>
              <div className="ce-why-text">
                <h3>{card.title}</h3>
                <p>{card.desc}</p>
                <div className="ce-why-actions">
                  <DownloadLink className="ce-why-cta" to={hongKongDownloadPath("hk_home_why", "free", search)}>
                    立即體驗<ArrowRight size={15} />
                  </DownloadLink>
                  <Link className="ce-why-contact" to={hongKongContactPath(undefined, "hk_home_why_contact", search)} onClick={() => reportLeadEntryConversion(search)}>
                    聯絡我們<Mail size={15} />
                  </Link>
                </div>
              </div>
              <div className="ce-why-visual">
                {card.preview === "pricing" ? <FreePlanPreview /> : <HomeProductPreview kind={card.preview} />}
                <ul className="ce-why-badges">
                  {card.chips.map((chip) => (
                    <li key={chip.text}>
                      <span style={{ backgroundColor: chip.color }}><chip.icon size={16} strokeWidth={1.9} aria-hidden="true" /></span>
                      {chip.text}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </div>
    </>
  );
}
